import { Injectable } from '@angular/core';
import { BackDocumentScanImage } from 'src/app/pages/identification/back-document-scan/back-document-scan-image';

import { BUSINESS_CONST } from '../../constants/business.constants';
import { EVENT } from '../../constants/dali.constants';
import { ExternalResponse } from '../../models/channel-communicator/external-response.model';
import { Changes, TagEvent } from '../../models/dali-tags.model';
import { DocumentModel } from '../../models/document.model';
import { LogDeviceModel } from '../../models/log-device.model';
import { OccupationModel } from '../../models/occupation.model';
import { AbreCuentasService } from '../abre-cuentas/abre-cuentas.service';
import { BitacoraService } from '../bitacora/bitacora.service';
import { ChannelCommunicatorService } from '../communicator/channel-communicator/channel-communicator.service';
import { PortalPnImplService } from '../communicator/channel-communicator/implementation/portalpn-impl.service';
import { BioCatchService } from '../external/biocatch/biocatch.service';
import { HelpersService } from '../helpers/helpers-onboarding.service';
import { LoaderService } from '../loader/loader.service';
import { NavigationService } from '../navigation/navigation-onboarding.service';
import { RequestService } from '../request/request-onboarding.service';
import { TaggingService } from '../tagging/tagging.service';
import { CryptoVaultService } from '../external/crypto-vault/crypto-vault.service';
import { Subject } from 'rxjs';

/**
 * Definición para que la clase sea vista desde root
 *
 * *Class definition to be seen from root*
 *
 * @type {Injectable}
 */
@Injectable({
  providedIn: 'root',
})

/**
 * Definición de la clase IdentificationService, servicio para enviar la información del documento
 *
 * *IdentificationService class definition, service to send documents info*
 *
 * @type {class}
 */
export class IdentificationService {

  statusSendBackDocument$ = new Subject<any>();

  /**
   * Constructor de la clase
   *
   * *Class Contstructor.*
   *
   * Inicializa los métodos para los modelos
   *
   * *Initialize method for models*
   *
   * @param {HelpersService}             helpersService
   * @param {RequestService}             requestService
   * @param {BioCatchService}            bioCatchService
   * @param {NavigationService}          navigationService
   * @param {AbreCuentasService}         abreCuentasService
   * @param {PortalPnImplService}        apppnImplService
   * @param {ChannelCommunicatorService} channelCommunicatorService
   *
   */
  constructor(
    private helpersService: HelpersService,
    private requestService: RequestService,
    private bioCatchService: BioCatchService,
    private navigationService: NavigationService,
    private abreCuentasService: AbreCuentasService,
    private apppnImplService: PortalPnImplService,
    private channelCommunicatorService: ChannelCommunicatorService,
    private taggingService: TaggingService,
    private bitacoraService: BitacoraService,
    private cipherService: CryptoVaultService,
    private loaderService: LoaderService) { }

  /**
   * Validación de aceptación de términos y condiciones, registro en la bítacora
   *
   * *Terms and conditions acceptance validation*
   */
  async termsAndConditions(): Promise<any> {
    const devicePrint = new LogDeviceModel();
    try {
      const ipBiocatch = this.bioCatchService.getCurrentScoreIp();
      devicePrint.device = this.abreCuentasService.getDeviceInfo();
      if (devicePrint !== undefined && devicePrint !== null) {
        devicePrint.device.ip = ipBiocatch;
        devicePrint.terms_type = 'term_condition_accept';
        devicePrint.device.event_name = 'term_condicion_acept';
        this.abreCuentasService.setdeviceInfo(devicePrint);
        return this.requestService.saveDataInBitacora(devicePrint);
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Envía la información de la parte de atrás del documento
   *
   * *Sends back document info*
   *
   * @type {function}
   * @returns {Promise}
   */
  async sendBackDocumentInfo(documentInfo: DocumentModel) {
    const product_type = this.abreCuentasService.getproductSelected().product_type;
    this.requestService.sendBackDocumentInfo(documentInfo).then(async (response: any) => {
      if (!response || !response.status || response.status === 500) {
        this.generatedError('500');
      } else if (response.status !== 200) {
        const codigo = response.error.codigo;
        const invalidDocument = 'Documento Invalido';
        switch (codigo) {
          case '422-01':
            this.generatedError('107');
            this.navigationService.pageLoadTag('No fue posible continuar la solicitud', ['pv10', 'pv111', 'pv71', '']);
            return;
          case '422-02':
          case '422-03':
            this.navigationService.pageLoadTag(invalidDocument, ['pv11', 'pv118', 'pv78', '']);
            this.generatedResponse('104');
            break;
          case '422-04':
          case '422-05':
            this.navigationService.pageLoadTag(invalidDocument, ['pv11', 'pv118', 'pv78', '']);
            this.generatedResponse('103');
            break;
          case '422-06':
            this.generatedError('106');
            return;
          default:
            this.generatedError('500');
            return;
        }
      } else {
        const dataResponse = await JSON.parse(this.cipherService.decrypt(response.body.data.message));
        console.log(dataResponse, 'responsedatad');
        this.abreCuentasService.setProspectResponse(dataResponse);
        this.validateDataClient(dataResponse, documentInfo);
        if (dataResponse.client && !this.abreCuentasService.getPostlogin()) {
          this.generatedError('108');
          const bitacoraEvent = `CN_is_Client`;
          this.bitacoraService.saveDataInBitacora(bitacoraEvent);
          this.bitacoraService.saveDataInBitacora('Cliente_en_listas_SI');
          return;
        }
        if (!this.abreCuentasService.getPostlogin()) {
          if (this.validateProcessForOldClients()) {
            this.generatedResponse('200');
          }
        }
        if (dataResponse.list_query_status.status_code === '5') {
          this.bitacoraService.saveDataInBitacora('Cliente_en_listas_SI');
          this.generatedError('107');
        }
      }
    }).catch(() => {
      this.generatedError('500');
    });
  }

  private generatedError(errorCode: string) {
    this.statusSendBackDocument$.next({
      code: errorCode,
      error: true,
    });
  }

  private generatedResponse(responseCode: string) {
    this.statusSendBackDocument$.next({
      code: responseCode,
      error: false,
    });
  }

  private validateDataClient(data: any, documentInfo: DocumentModel) {
    documentInfo.date_of_issue = data.expedition_date;
    documentInfo.issued_city = data.expedition_municipality;
    documentInfo.issued_department = data.dispatch_department;
    if (data.document.birthdate) {
      documentInfo.birthdate = data.document.birthdate;
    }
    if (data.document.first_name) {
      documentInfo.firstname = data.first_name;
    }
    if (data.document.second_lastname) {
      documentInfo.middlename = data.second_name;
    }
    this.abreCuentasService.setDocumentInfo(documentInfo);
    console.warn('Información actualizada: ', this.abreCuentasService.getDocumentInfo());
  }

  /**
   * Valida el product type y retorna el valor del successCode
   */
  getSuccessCodeByValidateProductType(product_type: string) {
    if (
      product_type === BUSINESS_CONST.PRODUCT_TYPE_REVOLVING ||
      product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE
    ) {
      this.navigationService.pageLoadTag(false, ['pv9', 'pv00', 'pv00', 'pv262']);
      return '105';
    }
    return '108';
  }

  /**
   * Valida el proceso para clientes antiguos
   *
   * *Process validation for old clients*
   *
   * @type {function}
   * @param {ExternalResponse} scanResponse
   */
  private validateProcessForOldClients(): any {
    const configsMap = this.abreCuentasService.getAbreCuentasConfigs();
    if (!configsMap.old_clients_enabled_for) { return true; }
    if (!this.abreCuentasService.getAppInfo().infoUnavailable) {
      const rules = [{
        rule_name: configsMap.old_clients_enabled_for,
        parameters: {
          channel_id: this.abreCuentasService.getAppInfo().channelId,
          mobile_platform: this.abreCuentasService.getAppInfo().platform,
          app_version: this.abreCuentasService.getAppInfo().appVersion,
        },
      }];

      this.requestService.validateRules(rules).then(
        async (rulesResponse: any) => {
          if (rulesResponse && rulesResponse.statusCode === 200) {
            await rulesResponse.data.rules_result.forEach((rule: any) => {
              if (configsMap.old_clients_enabled_for !== rule.rule_name) { return true; }
              if (!rule.result) {
                this.generatedResponse('105');
                return false;
              }
              return true;
            });
          } else {
            this.generatedError('500');
            return false;
          }
        },
      );
    }
  }

  /**
   * Escanea la parte de atrás del documento
   *
   * *Sends back document info*
   *
   * @type {function}
   * @returns {Promise}
   */
  async backDocumentScan(): Promise<any> {
    try {
      if (this.abreCuentasService.getAbreCuentasConfigs().old_version_apppn &&
        this.channelCommunicatorService.getChannelId() === this.apppnImplService.CHANNEL_ID) {
        const versionNumber = this.helpersService.versionToNumber(this.abreCuentasService.getAppInfo().appVersion);
        const oldVersionNumber = this.helpersService.versionToNumber(this.abreCuentasService.getAbreCuentasConfigs().old_version_apppn);
        if (versionNumber <= oldVersionNumber) {
          return await this.callBackDocumentScanOldVersions();
        }
      }
      return await this.callBackDocumentScanNewVersions();
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * Acción de respuesta del servicio de escaneo de la parte de atrás del documento en antiguas versiones
   *
   * *Response action of Back-document scanning service of old versions*
   *
   * @type {function}
   */
  private async callBackDocumentScanOldVersions(): Promise<any> {
    this.abreCuentasService.setDevicePermissions('camera');
    return new Promise(async (resolve, reject) => {
      await this.channelCommunicatorService.getImpl().backDocumentScan(
        this.abreCuentasService.getBytteConfig().licenses[this.channelCommunicatorService.getChannelId()].androidOld,
        this.abreCuentasService.getBytteConfig().licenses[this.channelCommunicatorService.getChannelId()].iosOld, '',
        this.abreCuentasService.getBytteConfig().timeouts.backDocumentScan.seconds)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Acción de respuesta del servicio de escaneo de la parte de atrás del documento en nuevas versiones
   *
   * *Response action of Back-document scanning service of new versions*
   *
   * @type {function}
   * @param {ExternalResponse} scanResponse
   */
  private async callBackDocumentScanNewVersions(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      await this.channelCommunicatorService.getImpl().verifyAndRequestPermissions().then(async responsePermission => {
        if (responsePermission.code && responsePermission.code === '200') {
          this.abreCuentasService.setDevicePermissions('camera');
          await this.channelCommunicatorService.getImpl().backDocumentScan(
            this.abreCuentasService.getBytteConfig().licenses[this.channelCommunicatorService.getChannelId()].android,
            this.abreCuentasService.getBytteConfig().licenses[this.channelCommunicatorService.getChannelId()].ios, this.abreCuentasService.getTransactionId(),
            this.abreCuentasService.getBytteConfig().timeouts.backDocumentScan.seconds)
            .then(response => resolve(response))
            .catch(err => reject(err));
        } else {
          this.navigationService.pageLoadTag('Necesitamos permisos para usar tu cámara', ['pv220', 'pv117', 'pv77']);
          this.navigationService.navigateToGeneralInformation('401');
          reject('Necesitamos permisos para usar tu cámara');
        }
      }).catch(errPermission => reject(errPermission));
    });
  }

  /**
   * Acción de respuesta del servicio de escaneo de la parte de atrás del documento
   *
   * *Back document scan service response action*
   *
   * @type {function}
   * @param {ExternalResponse} scanResponse
   */
  callbackBackDocumentScan(scanResponse: ExternalResponse) {
    const bitacoraEvent = `backdocumentScan_success`;
    this.bitacoraService.saveDataInBitacora(bitacoraEvent);
    this.navigationService.pageLoadTag(false, ['pv6', 'pv102', 'pv61', '']);
    const tagPageName = this.taggingService.getTagIdByProduct();
    if (!scanResponse.code) {
      this.navigationService.navigateToGeneralInformation('500');
      return;
    }
    switch (scanResponse.code) {
      case '400': // Wrong license activation key
      case '403': // Origin not authorized
      case '404': // Operation not found
      case '500': // Internal error
        // this.loaderService.hide();
        this.navigationService.navigateToGeneralInformation('500');
        return;
      case '504': // Failed attempt
        this.abreCuentasService.getAbreCuentasModel().backDocumentScanAttempts++;
        if (this.abreCuentasService.getAbreCuentasModel().backDocumentScanAttempts >=
          this.abreCuentasService.getBytteConfig().failedAttempts.backDocumentScan) {
          this.navigationService.pageLoadTag('No fue posible validad tu identidad', ['pv8', 'pv117', 'pv77', '']);
          this.abreCuentasService.caseInfoError('102');
          this.navigationService.nextStep('102');
        } else {
          const errorImage = {
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: BackDocumentScanImage,
          };
          this.abreCuentasService.setImageLottieError(errorImage);
          this.navigationService.pageLoadTag('No fue posible obtener la foto', ['pv7', 'pv110', 'pv70']);
          this.abreCuentasService.caseInfoError('111');
          this.navigationService.nextStep('111');
        }
        // this.loaderService.hide();
        return;
    }
    try {
      const changes: Changes[] = [
        {
          path: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionName`].path,
          value: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionName`].value,
        },
        {
          path: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionDetailsProductcategory`].path,
          value: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionDetailsProductcategory`].value,
        },
      ];
      const event: TagEvent = {
        category: {
          module: EVENT.customCategory.module.body,
          type: EVENT.customCategory.type.image,
          position: EVENT.customCategory.position.center,
        },
        action: EVENT.customAction.show,
        label: EVENT[`${tagPageName}E1customLabel`],
      };
      this.taggingService.pageTagEvent(event, changes);
    } catch (error) {
      // this.loaderService.hide();
      console.log(error);
    }
    console.warn('Back-Document: ', scanResponse);
    const documentInfo = new DocumentModel();
    let document_type = '';
    document_type = this.getDocumentType(scanResponse.data.document_type);
    documentInfo.document_type = document_type;
    documentInfo.card_number = scanResponse.data.card_number;
    documentInfo.document_number = parseInt(scanResponse.data.document_number, 10).toString();
    documentInfo.primary_lastname = this.abreCuentasService.converttoPascalCase(scanResponse.data.primary_lastname);
    documentInfo.second_lastname = this.abreCuentasService.converttoPascalCase(scanResponse.data.second_lastname);
    documentInfo.firstname = this.abreCuentasService.converttoPascalCase(scanResponse.data.firstname);
    documentInfo.middlename = this.abreCuentasService.converttoPascalCase(scanResponse.data.midlename);
    documentInfo.sex = this.getSexToString(scanResponse.data.sex);
    documentInfo.birthdate = scanResponse.data.birthdate;
    documentInfo.date_of_issue = scanResponse.data.date_of_issue;
    documentInfo.issued_city = scanResponse.data.issued_city;
    documentInfo.issued_department = scanResponse.data.issued_department;
    documentInfo.rh = scanResponse.data.blood_type_and_rh_factor;
    documentInfo.finger_printed = parseInt(scanResponse.data.signed_finger_print, 10);
    documentInfo.bar_code = scanResponse.data.bar_code_base64;
    documentInfo.back_document_picture = this.abreCuentasService.proccessBase64(scanResponse.data.back_document_photo);
    this.abreCuentasService.setDocumentInfo(documentInfo);
    console.warn('Datos Actualizados: ', this.abreCuentasService.getDocumentInfo());
    this.channelCommunicatorService.getImpl().sendDataToDSB(documentInfo.document_number);
    this.termsAndConditions();
    this.sendBackDocumentInfo(documentInfo);
    this.statusSendBackDocument$.subscribe((status: any) => {
      if (status.error) {
        this.navigationService.navigateToGeneralInformation(status.code);
      } else {
        this.navigationService.nextStep(status.code);
      }
    });
  }

  /**
   * Función para retornar el sexo como string
   * @param sex
   */
  getSexToString(sex: any) {
    return sex === 'M' ? 'M' : 'F';
  }

  /**
   * Función para evaluar el número de documento y esteblecer el valor en string
   * @param documentType
   */
  getDocumentType(documentType: any) {
    let document_type;
    switch (documentType) {
      case '02':
      case '03':
        document_type = 'CC';
        break;
      case 'I3':
        document_type = 'TI';
        break;
      default:
        document_type = 'CC';
        break;
    }
    return document_type;
  }

  /**
   * Función asíncrona para enviar la información de la parte frontal del documento
   *
   * *Async Function that sends fron document info*
   *
   * @type {function}
   * @param {string} documentPhoto
   * @param {string} facePhoto
   * @returns {Promise}
   */
  async sendFrontDocumentInfo(documentPhoto: string, facePhoto: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService
        .sendFrontDocumentInfo(documentPhoto, facePhoto)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  async sendFingerPrintsInfo(scan: ExternalResponse): Promise<any> {
    const data = scan.data;

    const body = { // cambiar el body
      index_fingerprint: {
        capture: this.abreCuentasService.proccessBase64(data.fingerPrints.index.capture),
        wsq_path: data.fingerPrints.index.wsqPath,
        minutia: data.fingerPrints.index.minutia,
      },
      middle_fingerprint: {
        capture: this.abreCuentasService.proccessBase64(data.fingerPrints.middle.capture),
        wsq_path: data.fingerPrints.middle.wsqPath,
        minutia: data.fingerPrints.middle.minutia,
      },
      ring_fingerprint: {
        capture: this.abreCuentasService.proccessBase64(data.fingerPrints.ring.capture),
        wsq_path: data.fingerPrints.ring.wsqPath,
        minutia: data.fingerPrints.ring.minutia,
      },
      pinky_fingerprint: {
        capture: this.abreCuentasService.proccessBase64(data.fingerPrints.pinky.capture),
        wsq_path: data.fingerPrints.pinky.wsqPath,
        minutia: data.fingerPrints.pinky.minutia,
      },
      hand_capture: this.abreCuentasService.proccessBase64(data.fingerPrints.hand.capture),
    };

    return new Promise((resolve, reject) => {
      this.requestService
        .sendFingerPrintsInfo(body)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  async getClientInformation(parms?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.requestService
        .getClientInformation(parms)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  async sendProspectOccupation(occupation: OccupationModel, document: DocumentModel): Promise<any> {
    const body = {
      occupation_id: occupation.id,
      ciiu: occupation.ciiu,
      document_id: document.document_number,
    };

    if (this.abreCuentasService.getproductSelected().code === '201') {
      const selectedAgreement = this.abreCuentasService.getAgreementSelected();
      body.occupation_id = 1;
      body.ciiu = '010';
      body['product_type'] = 'NOMINA';
      body['benefit_code'] = 'NODEFINIDO';

      if (selectedAgreement.transaction_type === 'true') {
        body['benefit_code'] = 'USAYNOPAGUES';
      }
    }

    const parms = {
      main_activity_id: occupation.id,
      first_name: this.abreCuentasService.getDocumentInfo().firstname,
      first_surname: this.abreCuentasService.getDocumentInfo().primary_lastname,
      product_type: BUSINESS_CONST.PRODUCT_TYPE_SAVINGS,
    };

    return new Promise((resolve, reject) => {
      this.requestService
        .sendProspectOccupation(body, parms)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  /**
   * Consulta por cedula el cliente
   *
   * @type {function}
   * @returns {Promise}
   */
  async validateIfClientExists(documentInfo: DocumentModel) {
    try {
      const response = await this.requestService.sendBackDocumentInfo(documentInfo);
      if (response.status === 200) {
        return await JSON.parse(this.cipherService.decrypt(response.body.data.message));
      }
      return { notFound: true };
    } catch (error) {
      this.navigationService.navigateToGeneralInformation('131');
    }
  }

  /**
   * Consulta base de datos relacionados
   *
   * @type {function}
   * @returns {Promise}
   */
  async validateIfIsRelated(documentId: string) {
    try {
      const response = await this.requestService.checkIfIsRelated(documentId);
      if (response.status === 200) {
        return response.body;
      }
      return { notFound: true };
    } catch (error) {
      // BITÁCORA CON employee_members_fail
      const bitacoraEvent = `employee_members_fail`;
      this.bitacoraService.saveDataInBitacora(bitacoraEvent);
      this.navigationService.navigateToGeneralInformation('tech');
    }
  }

}
